/**
 * Security step 1: account lockout + two-step sign-in with an authenticator app (TOTP).
 * One row per key: [key, English, 中文]; Malay falls back to English. Registered in ../modules.ts.
 */
const ROWS = [
  ["sec.locked","Too many attempts. Try again in {minutes} minutes.","尝试次数过多，请在 {minutes} 分钟后再试。"],
  ["sec.2fa.title","Two-step sign-in","两步验证登录"],
  ["sec.2fa.enterTitle","Enter your code","输入验证码"],
  ["sec.2fa.enterBody","Open your authenticator app (Google Authenticator, Microsoft Authenticator …) and enter the 6-digit code for Senvia POS.","打开身份验证器应用（Google Authenticator、Microsoft Authenticator 等），输入 Senvia POS 的 6 位验证码。"],
  ["sec.2fa.code","6-digit code","6 位验证码"],
  ["sec.2fa.useRecovery","Lost your phone? Enter one of your recovery codes instead.","手机丢失？可改为输入一个恢复码。"],
  ["sec.2fa.verify","Verify","验证"],
  ["sec.2fa.back","Back to sign in","返回登录"],
  ["sec.2fa.invalid","The code is not correct. Check the time on your phone and try again.","验证码不正确。请检查手机时间后重试。"],
  ["sec.2fa.expired","This sign-in has expired. Please sign in again.","本次登录已过期，请重新登录。"],
  ["sec.2fa.setupTitle","Set up two-step sign-in","设置两步验证登录"],
  ["sec.2fa.setupBody","Platform admin accounts must use an authenticator app. Scan this QR code with Google Authenticator (or any authenticator app), then enter the 6-digit code it shows.","平台管理员账户必须使用身份验证器应用。请用 Google Authenticator（或任意身份验证器应用）扫描此二维码，然后输入应用显示的 6 位验证码。"],
  ["sec.2fa.setupBodyOwner","Scan this QR code with Google Authenticator (or any authenticator app), then enter the 6-digit code it shows.","请用 Google Authenticator（或任意身份验证器应用）扫描此二维码，然后输入应用显示的 6 位验证码。"],
  ["sec.2fa.manual","Can't scan? Enter this key in the app:","无法扫码？请在应用中手动输入此密钥："],
  ["sec.2fa.openApp","Open in authenticator app","在身份验证器应用中打开"],
  ["sec.2fa.confirm","Confirm and continue","确认并继续"],
  ["sec.rc.title","Save your recovery codes","保存您的恢复码"],
  ["sec.rc.body","If you lose your phone, each of these codes lets you sign in once. They are shown only now — copy them or write them down and keep them somewhere safe.","如果手机丢失，每个恢复码可登录一次。恢复码只显示这一次 —— 请复制或抄写下来，妥善保存。"],
  ["sec.rc.copy","Copy codes","复制恢复码"],
  ["sec.rc.copied","Copied","已复制"],
  ["sec.rc.saved","I saved them — continue","我已保存 —— 继续"],
  ["sec.acct.title","Two-step sign-in (authenticator app)","两步验证登录（身份验证器应用）"],
  ["sec.acct.offBody","Protect your login with a 6-digit code from Google Authenticator in addition to your password.","除密码外，再用 Google Authenticator 的 6 位验证码保护您的登录。"],
  ["sec.acct.onBody","On since {date}. Every sign-in asks for the code from your authenticator app. Recovery codes left: {n}.","已于 {date} 开启。每次登录都需要输入身份验证器应用中的验证码。剩余恢复码：{n} 个。"],
  ["sec.acct.required","Required for platform admins — it can't be switched off. Lost phone: ask the operator to run reset-2fa.","平台管理员必须开启，无法关闭。手机丢失：请运维人员执行 reset-2fa。"],
  ["sec.acct.turnOn","Turn on","开启"],
  ["sec.acct.turnOff","Turn off","关闭"],
  ["sec.acct.newCodes","New recovery codes","重新生成恢复码"],
  ["sec.acct.password","Password","密码"],
  ["sec.acct.enabled","Two-step sign-in is on.","两步验证登录已开启。"],
  ["sec.acct.disabled","Two-step sign-in is off.","两步验证登录已关闭。"],
  ["sec.pa.unlock","Unlock sign-in","解锁登录"],
  ["sec.pa.unlocked","Sign-in unlocked for {email}.","已解锁 {email} 的登录。"],
  ["sec.pa.notLocked","{email} was not locked.","{email} 未被锁定。"],
  ["apiError.login_locked","Too many attempts. Try again in {minutes} minutes.","尝试次数过多，请在 {minutes} 分钟后再试。"],
  ["apiError.invalid_code","The code is not correct. Check the time on your phone and try again.","验证码不正确。请检查手机时间后重试。"],
  ["apiError.challenge_expired","This sign-in has expired. Please sign in again.","本次登录已过期，请重新登录。"],
  ["apiError.two_factor_required","Two-step sign-in is required for platform admins.","平台管理员必须开启两步验证登录。"],
  ["sec.pa.lockedNow","Sign-in locked until {time} (too many wrong passwords).","登录已锁定至 {time}（密码错误次数过多）。"],
] as const;

export type SecKey = (typeof ROWS)[number][0];
const col = (i: 1 | 2) => Object.fromEntries(ROWS.map((r) => [r[0], r[i]])) as Record<SecKey, string>;

export const taskSEC = { en: col(1), zh: col(2), ms: col(1) };
